import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const Navbar: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLogoClick = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Handle scrolling to section after navigation
  useEffect(() => {
    const hash = location.hash;
    if (hash && location.pathname === '/') {
      const timer = setTimeout(() => {
        const element = document.getElementById(hash.slice(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100); // Small delay to ensure page is rendered
      
      return () => clearTimeout(timer);
    }
  }, [location]);

  const handleSectionClick = (sectionId: string) => {
    // Close mobile menu when clicking on section links
    setIsMobileMenuOpen(false);
    
    if (location.pathname === '/') {
      // Already on home page, just scroll
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to home page with hash
      navigate(`/#${sectionId}`);
    }
  };

  const handleServiceClick = () => {
    // Close mobile menu when clicking on service links
    setIsMobileMenuOpen(false);
  };

  const services = [
    { href: "/villanyszereles", text: "Villanyszerelés" },
    { href: "/napelem", text: "Napelem" },
    { href: "/klima", text: "Klíma" },
    { href: "/berendezesgyartas", text: "Berendezés gyártás" },
    { href: "/villamostervezes", text: "Villamos tervezés" },
    { href: "/rendezvenyek", text: "Rendezvények" }
  ];

  const menuItems = [
    { id: "about", text: "Rólunk" },
    { id: "testimonials", text: "Vélemények" },
    { id: "contact", text: "Elérhetőség" }
  ];

  return (
    <header className="bg-white/95 backdrop-blur-md shadow-lg sticky top-0 z-50 border-b border-red-600/10">
      <div className="container mx-auto px-4 py-3 flex justify-between items-center">
        <Link to="/" className="flex items-center group cursor-pointer" onClick={handleLogoClick}>
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
              P&K
            </h1>
            <h2 className="text-xl font-bold text-black">
              ELECTRO
            </h2>
          </div>
        </Link>
        
        {/* Desktop Navigation */}
        <nav className="hidden lg:flex flex-1 justify-end">
          <ul className="flex space-x-2 items-center flex-nowrap">
            {/* Service Links */}
            {services.map((service, index) => (
              <li key={index} className="relative group flex-shrink-0">
                <Link
                  to={service.href}
                  className="text-black font-medium text-base hover:text-red-600 transition-all duration-300 relative py-2 px-2 rounded hover:bg-red-50 whitespace-nowrap"
                >
                  {service.text}
                  {/* Hover underline effect */}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-red-600 transition-all duration-300 group-hover:w-full"></span>
                  {/* Glow effect */}
                  <span className="absolute inset-0 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-[0_0_15px_rgba(220,38,38,0.2)]"></span>
                </Link>
              </li>
            ))}
            
            {/* Section Links */}
            {menuItems.map((item, index) => (
              <li key={index + services.length} className="relative group flex-shrink-0">
                <button
                  onClick={() => handleSectionClick(item.id)}
                  className="text-black font-medium text-base hover:text-red-600 transition-all duration-300 relative py-2 px-2 rounded hover:bg-red-50 whitespace-nowrap"
                >
                  {item.text}
                  {/* Hover underline effect */}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-red-600 transition-all duration-300 group-hover:w-full"></span>
                  {/* Glow effect */}
                  <span className="absolute inset-0 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-[0_0_15px_rgba(220,38,38,0.2)]"></span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Mobile Menu Button */}
        <button
          onClick={toggleMobileMenu}
          className="lg:hidden relative w-8 h-8 flex flex-col justify-center items-center group"
        >
          <span className={`w-6 h-0.5 bg-black transition-all duration-300 ${isMobileMenuOpen ? 'rotate-45 translate-y-1.5' : ''}`}></span>
          <span className={`w-6 h-0.5 bg-black mt-1 transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0' : ''}`}></span>
          <span className={`w-6 h-0.5 bg-black mt-1 transition-all duration-300 ${isMobileMenuOpen ? '-rotate-45 -translate-y-1.5' : ''}`}></span>
        </button>
      </div>

      {/* Mobile Navigation */}
      <div className={`lg:hidden ${isMobileMenuOpen ? 'block' : 'hidden'}`}>
        <nav className="px-4 pt-2 pb-4 bg-white/95 backdrop-blur-md border-t border-red-600/10">
          <ul className="space-y-2">
            {/* Mobile Service Links */}
            {services.map((service, index) => (
              <li key={index}>
                <Link
                  to={service.href}
                  onClick={handleServiceClick}
                  className="block py-2 px-4 text-black hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-300"
                >
                  {service.text}
                </Link>
              </li>
            ))}
            
            {/* Mobile Section Links */}
            {menuItems.map((item, index) => (
              <li key={index + services.length}>
                <button
                  onClick={() => handleSectionClick(item.id)}
                  className="block w-full text-left py-2 px-4 text-black hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-300"
                >
                  {item.text}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default Navbar; 